import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import api from '@/utils/api';
import { toast } from 'sonner';
import { ArrowLeft, Upload, Trash2, Edit2, Image, Check, X, Loader2, AlertCircle } from 'lucide-react';
import { uploadFileToS3, extractFileKey } from '@/utils/s3Upload';
import { GalleryCaptionCard } from '@/components/teacher/GalleryCaptionCard';

const MAX_GALLERY_IMAGES = 30;

const TeacherGalleryPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [maxImages, setMaxImages] = useState(MAX_GALLERY_IMAGES);
  const [canUpload, setCanUpload] = useState(true);
  const [isPublic, setIsPublic] = useState(false);
  const [caption, setCaption] = useState('');
  
  // Upload state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [pendingFiles, setPendingFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [currentUploadIndex, setCurrentUploadIndex] = useState(0);
  
  // Edit state
  const [editingImage, setEditingImage] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Delete state
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/teacher/gallery');
      setImages(response.data.images || []);
      setTotalCount(response.data.total_count || 0);
      setMaxImages(response.data.max_images || MAX_GALLERY_IMAGES);
      setCanUpload(response.data.can_upload !== false);
      setIsPublic(response.data.is_public || false);
      setCaption(response.data.caption || '');
    } catch (error) {
      toast.error('Failed to load gallery');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    // Calculate how many more images can be uploaded
    const remainingSlots = maxImages - totalCount;
    
    if (remainingSlots <= 0) {
      toast.error(`Maximum of ${maxImages} images reached`);
      return;
    }
    
    // Limit to remaining slots
    const filesToUpload = files.slice(0, remainingSlots);
    
    if (files.length > remainingSlots) {
      toast.warning(`Only ${remainingSlots} image${remainingSlots !== 1 ? 's' : ''} can be added. ${files.length - remainingSlots} file${files.length - remainingSlots !== 1 ? 's' : ''} skipped.`);
    }
    
    // Validate each file
    const validFiles = [];
    const newPreviews = [];
    
    for (const file of filesToUpload) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image file`);
        continue;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 10MB)`);
        continue;
      }
      
      validFiles.push(file);
      newPreviews.push(URL.createObjectURL(file));
    }
    
    if (validFiles.length > 0) {
      setPendingFiles(validFiles);
      setPreviewUrls(newPreviews);
      setUploadTitle('');
      setUploadDescription('');
      setCurrentUploadIndex(0);
    }
  };

  const handleUpload = async () => {
    if (pendingFiles.length === 0) return;
    
    setIsUploading(true);
    setUploadProgress(0);
    setCurrentUploadIndex(0);
    
    let successCount = 0;
    let currentCount = totalCount;
    
    for (let i = 0; i < pendingFiles.length; i++) {
      // Check if we've hit the max
      if (currentCount >= maxImages) {
        toast.warning(`Maximum of ${maxImages} images reached`);
        break;
      }
      
      setCurrentUploadIndex(i);
      const file = pendingFiles[i];
      
      try {
        // Upload to R2
        const { promise } = uploadFileToS3(file, 'gallery/', (progress) => {
          const overallProgress = ((i + progress / 100) / pendingFiles.length) * 100;
          setUploadProgress(Math.round(overallProgress));
        });
        
        const url = await promise;
        const fileKey = extractFileKey(url);
        
        // Save to backend (only add title/description to first image)
        await api.post('/teacher/gallery', {
          url,
          file_key: fileKey,
          title: i === 0 ? (uploadTitle || null) : null,
          description: i === 0 ? (uploadDescription || null) : null
        });
        
        successCount++;
        currentCount++;
      } catch (error) {
        // Show server error message if available
        const errorMessage = error.response?.data?.detail || `Failed to upload ${file.name}`;
        toast.error(errorMessage);
        
        // Stop if max reached
        if (error.response?.status === 400 && errorMessage.includes('Maximum')) {
          break;
        }
      }
    }
    
    if (successCount > 0) {
      toast.success(`${successCount} image${successCount !== 1 ? 's' : ''} added to gallery`);
    }
    
    // Reset upload state
    setPendingFiles([]);
    setPreviewUrls([]);
    setUploadTitle('');
    setUploadDescription('');
    setCurrentUploadIndex(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    // Refresh gallery
    fetchGallery();
    setIsUploading(false);
    setUploadProgress(0);
  };

  const cancelUpload = () => {
    // Revoke object URLs
    previewUrls.forEach(url => URL.revokeObjectURL(url));
    
    setPendingFiles([]);
    setPreviewUrls([]);
    setUploadTitle('');
    setUploadDescription('');
    setCurrentUploadIndex(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    
    setDeletingId(imageId);
    try {
      await api.delete(`/teacher/gallery/${imageId}`);
      toast.success('Image deleted');
      fetchGallery();
    } catch (error) {
      toast.error('Failed to delete image');
    } finally {
      setDeletingId(null);
    }
  };

  const openEditDialog = (image) => {
    setEditingImage(image);
    setEditTitle(image.title || '');
    setEditDescription(image.description || '');
  };

  const handleUpdate = async () => {
    if (!editingImage) return;
    
    setIsUpdating(true);
    try {
      await api.put(`/teacher/gallery/${editingImage.id}?title=${encodeURIComponent(editTitle)}&description=${encodeURIComponent(editDescription)}`);
      toast.success('Image updated');
      setEditingImage(null);
      fetchGallery();
    } catch (error) {
      toast.error('Failed to update image');
    } finally {
      setIsUpdating(false);
    }
  };

  const isAtMaxCapacity = totalCount >= maxImages;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF9F6' }}>
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#163329' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6" style={{ backgroundColor: '#FAF9F6' }}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/teacher')}
            className="rounded-full"
            data-testid="back-button"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>

        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
            My Gallery
          </h1>
          <p className="text-sm mt-1" style={{ color: '#5C635A' }}>
            Showcase your work to learners
          </p>
        </div>

        {/* Image Count Card */}
        <Card className="mb-6" style={{ borderColor: '#E3E1DB' }}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium" style={{ color: '#163329' }}>
                You have {totalCount} image{totalCount !== 1 ? 's' : ''}
              </span>
              <span className={`text-xs font-medium px-2 py-1 rounded-full ${isPublic ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                {isPublic ? 'Public' : 'No images yet'}
              </span>
            </div>
            {isAtMaxCapacity && (
              <p className="text-xs mt-2 flex items-center gap-1 text-amber-600">
                <AlertCircle className="h-3 w-3" />
                Maximum of {maxImages} images reached
              </p>
            )}
          </CardContent>
        </Card>

        <GalleryCaptionCard initialCaption={caption} />

        {/* Upload Area */}
        <Card className="mb-6" style={{ borderColor: '#E3E1DB' }}>
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
              <Upload className="inline h-5 w-5 mr-2" />
              Add Images
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isAtMaxCapacity ? (
              <div className="text-center py-8 rounded-lg" style={{ backgroundColor: '#FEF3C7' }}>
                <AlertCircle className="h-12 w-12 mx-auto mb-3 text-amber-500" />
                <p className="font-medium text-amber-700">Maximum of {maxImages} images reached</p>
                <p className="text-sm mt-1 text-amber-600">Delete some images to upload new ones</p>
              </div>
            ) : pendingFiles.length === 0 ? (
              <div 
                className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-opacity-70 transition-colors"
                style={{ borderColor: '#163329', backgroundColor: '#F5F5F0' }}
                onClick={() => fileInputRef.current?.click()}
                data-testid="upload-dropzone"
              >
                <Image className="h-12 w-12 mx-auto mb-3" style={{ color: '#163329', opacity: 0.5 }} />
                <p className="font-medium" style={{ color: '#163329' }}>Click to select images</p>
                <p className="text-sm mt-1" style={{ color: '#5C635A' }}>PNG, JPG, WEBP up to 10MB each</p>
                <p className="text-xs mt-2" style={{ color: '#9CA3AF' }}>You can select multiple images</p>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="gallery-file-input"
                />
              </div>
            ) : (
              <div className="space-y-4">
                {/* Preview Grid */}
                <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                  {previewUrls.map((url, idx) => (
                    <div 
                      key={idx} 
                      className={`aspect-square rounded-lg overflow-hidden bg-gray-100 relative ${isUploading && idx === currentUploadIndex ? 'ring-2 ring-green-500' : ''}`}
                    >
                      <img 
                        src={url} 
                        alt={`Preview ${idx + 1}`} 
                        className="w-full h-full object-cover"
                      />
                      {isUploading && idx < currentUploadIndex && (
                        <div className="absolute inset-0 bg-green-500/30 flex items-center justify-center">
                          <Check className="h-6 w-6 text-white" />
                        </div>
                      )}
                      {isUploading && idx === currentUploadIndex && (
                        <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                          <Loader2 className="h-6 w-6 text-white animate-spin" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                <p className="text-sm" style={{ color: '#5C635A' }}>
                  {pendingFiles.length} image{pendingFiles.length !== 1 ? 's' : ''} selected
                </p>
                
                {pendingFiles.length === 1 && (
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="upload-title">Title (optional)</Label>
                      <Input
                        id="upload-title"
                        value={uploadTitle}
                        onChange={(e) => setUploadTitle(e.target.value)}
                        placeholder="e.g., Client Hair Styling"
                        className="mt-1"
                        disabled={isUploading}
                        data-testid="upload-title-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="upload-description">Description (optional)</Label>
                      <Textarea
                        id="upload-description"
                        value={uploadDescription}
                        onChange={(e) => setUploadDescription(e.target.value)}
                        placeholder="Brief description of the image..."
                        rows={2}
                        className="mt-1"
                        disabled={isUploading}
                        data-testid="upload-description-input"
                      />
                    </div>
                  </div>
                )}
                
                {isUploading && (
                  <div className="space-y-2">
                    <Progress value={uploadProgress} className="h-2" />
                    <p className="text-sm text-center" style={{ color: '#5C635A' }}>
                      Uploading {currentUploadIndex + 1} of {pendingFiles.length}... {uploadProgress}%
                    </p>
                  </div>
                )}
                
                <div className="flex gap-3 justify-end">
                  <Button
                    variant="outline"
                    onClick={cancelUpload}
                    disabled={isUploading}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Cancel
                  </Button>
                  <Button
                    onClick={handleUpload}
                    disabled={isUploading}
                    style={{ backgroundColor: '#163329' }}
                    data-testid="upload-button"
                  >
                    {isUploading ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading...</>
                    ) : (
                      <><Check className="h-4 w-4 mr-2" />Upload {pendingFiles.length} Image{pendingFiles.length !== 1 ? 's' : ''}</>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Gallery Grid */}
        <Card style={{ borderColor: '#E3E1DB' }}>
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
              <Image className="inline h-5 w-5 mr-2" />
              Your Images ({totalCount})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {images.length === 0 ? (
              <div className="text-center py-12" style={{ color: '#5C635A' }}>
                <Image className="h-16 w-16 mx-auto mb-4 opacity-30" />
                <p className="font-medium">No images yet</p>
                <p className="text-sm mt-1">Upload your first image to start building your gallery</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {images.map((image) => (
                  <div 
                    key={image.id} 
                    className="relative group rounded-lg overflow-hidden bg-gray-100"
                    style={{ aspectRatio: '1' }}
                    data-testid={`gallery-image-${image.id}`}
                  >
                    <img
                      src={image.presigned_url || image.url}
                      alt={image.title || 'Gallery image'}
                      className="w-full h-full object-cover"
                    />
                    
                    {/* Overlay with actions */}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => openEditDialog(image)}
                        className="h-8 w-8 p-0"
                        title="Edit"
                        data-testid={`edit-image-${image.id}`}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(image.id)}
                        disabled={deletingId === image.id}
                        className="h-8 w-8 p-0"
                        title="Delete"
                        data-testid={`delete-image-${image.id}`}
                      >
                        {deletingId === image.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    
                    {/* Title overlay */}
                    {image.title && (
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                        <p className="text-white text-xs font-medium truncate">{image.title}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={!!editingImage} onOpenChange={(open) => !open && setEditingImage(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                Edit Image Details
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {editingImage && (
                <div className="w-full h-40 rounded-lg overflow-hidden bg-gray-100 mb-4">
                  <img
                    src={editingImage.presigned_url || editingImage.url}
                    alt="Edit preview"
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div>
                <Label htmlFor="edit-title">Title</Label>
                <Input
                  id="edit-title"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  placeholder="Image title"
                  className="mt-1"
                  data-testid="edit-title-input"
                />
              </div>
              <div>
                <Label htmlFor="edit-description">Description</Label>
                <Textarea
                  id="edit-description"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  placeholder="Image description"
                  rows={3}
                  className="mt-1"
                  data-testid="edit-description-input"
                />
              </div>
              <div className="flex gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setEditingImage(null)}
                  disabled={isUpdating}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUpdate}
                  disabled={isUpdating}
                  style={{ backgroundColor: '#163329' }}
                  data-testid="save-edit-button"
                >
                  {isUpdating ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default TeacherGalleryPage;
